import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRemainingTime } from './EnglishProficiencyTest.helpers';
import styles from './EnglishProficiencyTimer.module.scss';

/** Below this the countdown turns amber. */
const WARNING_THRESHOLD_SECONDS = 5 * 60;

interface IEnglishProficiencyTimer {
  /** Wall-clock instant the sitting closes, in epoch milliseconds. */
  deadlineAtMs: number;
  onExpire: () => void;
}

const secondsUntil = (deadlineAtMs: number): number =>
  Math.max(0, Math.ceil((deadlineAtMs - Date.now()) / 1000));

/**
 * The whole-test clock.
 *
 * Anchored to a wall-clock deadline rather than counting ticks: over an hour a
 * decrementing interval drifts badly, and a backgrounded tab has its timers
 * throttled to once a minute, so a candidate who leaves the page and comes
 * back would see a clock minutes behind the server's. Every tick recomputes
 * from `Date.now()`, so a missed tick costs accuracy nothing.
 *
 * The deadline itself is re-derived from the server's `remainingSeconds` on
 * every save, which is where the correction comes from.
 */
const EnglishProficiencyTimer: FC<IEnglishProficiencyTimer> = ({
  deadlineAtMs,
  onExpire,
}) => {
  const { t } = useTranslation();
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    secondsUntil(deadlineAtMs)
  );
  const hasExpiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    const tick = () => {
      const remaining = secondsUntil(deadlineAtMs);
      setRemainingSeconds(remaining);
      if (remaining <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        onExpireRef.current();
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    // A throttled tab can miss a minute of ticks; catch up the moment it is
    // looked at again rather than on the next scheduled tick.
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [deadlineAtMs]);

  return (
    <div
      className={clsx(styles.timer, {
        [styles.timerWarning]:
          remainingSeconds > 0 && remainingSeconds <= WARNING_THRESHOLD_SECONDS,
      })}
      role="timer"
      aria-label={t('englishProficiencyTest.timeRemaining')}
      data-testid="english-proficiency-test--timer"
    >
      <span className={styles.timerLabel}>
        {t('englishProficiencyTest.timeRemaining')}
      </span>
      <span className={styles.timerValue}>
        {formatRemainingTime(remainingSeconds)}
      </span>
    </div>
  );
};

export default EnglishProficiencyTimer;
